/**
 * Mini-Project Roles — settings-modal section.
 *
 * DEV-1088 / Roles Phase 1. Sits inside the Team tab, below the Members
 * list. Two operations the Lead does here:
 *   1. Define roles (name, description, responsibilities, skills,
 *      headcount). Add Role opens a right-side Tray with the form.
 *   2. Invite users to fill roles. Invite opens a right-side Tray with
 *      the same typeahead pattern as Add Member, plus an optional
 *      message field. The invitee gets a bell notification and an
 *      Action Center row to accept or decline.
 *
 * State model: per-mutation, server-side. Unlike the Members section
 * which buffers edits and bulk-saves on modal Save, every role action
 * (create / update / delete / invite / revoke / remove) commits to the
 * server immediately. Local cache lives in
 * src/Data/MiniProjectConfig/miniProjectRoles.ts and re-renders fire on
 * the l4e_miniproject_roles_changed event.
 *
 * Permissions: Lead-only. The Settings modal already gates by Lead;
 * this component does not re-check.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { Tray } from "../Tray/Tray";
import FlipTeamMemberCard from "../TeamMemberCard/FlipTeamMemberCard";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import { mediaSrc } from "../../api/mediaUrl";
import {
  type MiniProjectRole,
  type MiniProjectRoleAssignee,
  createMiniProjectRole,
  updateMiniProjectRole,
  deleteMiniProjectRole,
  inviteToMiniProjectRole,
  endMiniProjectRoleAssignment,
} from "../../api/miniProjectRoles";
import { updateMiniProjectTeamMember } from "../../api/miniProjects";
import {
  getRolesForMiniProject,
  hydrateMiniProjectRolesFromApi,
  refetchMiniProjectRoles,
  upsertRole,
  removeRole,
} from "../../Data/MiniProjectConfig/miniProjectRoles";
import {
  getTeamForMiniProject,
  upsertTeamMember,
  type LocalMiniProjectTeamMember,
} from "../../Data/MiniProjectConfig/miniProjectTeam";

interface Props {
  miniProjectGuid: string;
  currentUserGuid: string;
}

interface L4eUser {
  userGuid: string;
  displayName?: string | null;
  name?: string | null;
  email?: string | null;
  screenName?: string | null;
  profileImageUrl?: string | null;
}

type RoleDraft = {
  name: string;
  description: string;
  responsibilities: string;
  skillsDesired: string;
  headcountNeeded: string; // free-text in the form; parsed on save
};

type TrayMode =
  | null
  | "add-role"
  | { editRoleGuid: string }
  | { inviteToRoleGuid: string }
  // Skip's UX consistency request — clicking an assignee chip opens this
  // tray so the assignee is shown the same way as a Team member is shown
  // when their card is clicked from the Members list: FlipTeamMemberCard
  // preview (which surfaces the Impact Signature on the back face), the
  // role name as a read-only label, the hide-from-public checkbox, and
  // Remove / Cancel / Save in the footer.
  | { assigneeRoleGuid: string; assigneeAssignmentGuid: string };

const DESC_MAX = 1000;

/* ──────────────────────────────────────────────────────────────────
 *  Styles — match MiniProjectTeamTab.tsx so the section nests
 *  visually without breaking the modal's design rhythm.
 * ────────────────────────────────────────────────────────────────── */

const styles = {
  section: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
    marginBlockStart: 8,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "var(--text-primary)",
  },
  subtitle: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    marginBlockStart: 4,
  },
  counter: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    fontWeight: 500,
    marginInlineStart: 12,
  },
  addButton: {
    background: "var(--accent-secondary)",
    border: "none",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
    cursor: "pointer",
  },
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 8,
  },
  emptyState: {
    padding: 20,
    textAlign: "center" as const,
    color: "var(--text-tertiary)",
    fontSize: 13,
    background: "var(--bg-surface)",
    border: "1px dashed var(--border-default)",
    borderRadius: 8,
  },
  card: {
    background: "var(--bg-surface)",
    border: "1px solid var(--border-default)",
    borderRadius: 8,
    padding: 12,
    display: "flex",
    flexDirection: "column" as const,
    gap: 8,
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap" as const,
  },
  cardName: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--text-primary)",
  },
  templateChip: {
    fontSize: 10,
    fontWeight: 500,
    padding: "2px 6px",
    borderRadius: 8,
    background: "var(--bg-elevated)",
    color: "var(--text-tertiary)",
  },
  countChip: {
    fontSize: 11,
    fontWeight: 500,
    padding: "2px 8px",
    borderRadius: 8,
    background: "var(--bg-elevated)",
    color: "var(--text-secondary)",
  },
  description: {
    fontSize: 12,
    color: "var(--text-secondary)",
    lineHeight: 1.5,
  },
  cardActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  iconButton: {
    background: "transparent",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    color: "var(--text-secondary)",
    fontSize: 12,
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  iconButtonDanger: {
    color: "var(--color-error)",
  },
  inviteButton: {
    background: "var(--bg-hover)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 10px",
    cursor: "pointer",
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 6,
    marginBlockStart: 4,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 4px 3px 4px",
    border: "1px solid var(--border-default)",
    borderRadius: 100,
    fontSize: 11,
    background: "var(--bg-elevated)",
    color: "var(--text-primary)",
  },
  chipPending: {
    borderStyle: "dashed" as const,
    borderColor: "var(--accent-secondary)",
  },
  chipAvatar: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "var(--bg-hover)",
    color: "var(--text-secondary)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 9,
    flexShrink: 0,
  },
  chipName: {
    fontSize: 11,
    color: "var(--text-primary)",
  },
  chipStatus: {
    fontSize: 9,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: 0.3,
    padding: "1px 6px",
    borderRadius: 100,
  },
  chipStatusActive: {
    background: "var(--color-success-bg, rgba(34, 197, 94, 0.15))",
    color: "var(--color-success)",
  },
  chipStatusPending: {
    background: "var(--color-warning-bg, rgba(245, 158, 11, 0.15))",
    color: "var(--color-warning)",
  },
  chipRemove: {
    background: "transparent",
    border: "none",
    color: "var(--text-tertiary)",
    width: 16,
    height: 16,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 10,
    marginInlineEnd: 3,
  },
  chipsEmpty: {
    fontSize: 11,
    color: "var(--text-tertiary)",
    fontStyle: "italic" as const,
    marginBlockStart: 4,
  },
  trayBody: {
    padding: 20,
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
  },
  trayFooter: {
    padding: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-tertiary)",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    marginBlockEnd: 4,
  },
  fieldHelp: {
    fontSize: 11,
    color: "var(--text-tertiary)",
    fontWeight: 400,
    textTransform: "none" as const,
    marginInlineStart: 6,
    letterSpacing: 0,
  },
  inlineInput: {
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    padding: "6px 10px",
    color: "var(--text-primary)",
    fontSize: 13,
    width: "100%" as const,
  },
  textarea: {
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    padding: "8px 10px",
    color: "var(--text-primary)",
    fontSize: 13,
    width: "100%" as const,
    minHeight: 70,
    resize: "vertical" as const,
    fontFamily: "inherit",
  },
  helpText: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    lineHeight: 1.4,
  },
  smallButton: {
    background: "var(--bg-hover)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    color: "var(--text-secondary)",
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 10px",
    cursor: "pointer",
  },
  dangerButton: {
    color: "var(--color-error)",
  },
  primaryButton: {
    background: "var(--accent-bg, var(--bg-emphasis, var(--text-primary)))",
    border: "1px solid var(--accent-border, var(--text-primary))",
    borderRadius: 6,
    color: "var(--bg-elevated)",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 16px",
    cursor: "pointer",
  },
  userResults: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 0,
    maxBlockSize: 260,
    overflowY: "auto" as const,
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    background: "var(--bg-surface)",
  },
  userResultEmpty: {
    padding: 16,
    textAlign: "center" as const,
    fontSize: 12,
    color: "var(--text-tertiary)",
  },
  userResultItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    background: "transparent",
    border: "none",
    borderBlockEnd: "1px solid var(--border-subtle, var(--border-default))",
    cursor: "pointer",
    textAlign: "start" as const,
    width: "100%" as const,
  },
  userResultAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--text-secondary)",
    fontSize: 12,
    fontWeight: 600,
    flexShrink: 0,
    overflow: "hidden",
  },
  userResultText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
  },
  userResultName: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  userResultEmail: {
    fontSize: 11,
    color: "var(--text-tertiary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
};

/* ──────────────────────────────────────────────────────────────────
 *  Main component
 * ────────────────────────────────────────────────────────────────── */

const MiniProjectRolesSection: React.FC<Props> = ({ miniProjectGuid, currentUserGuid }) => {
  const { t } = useAppTranslation("projectConfig");
  // DEV-1088 bugfix — useApiSecurityData destructures `securityUsers`
  // (NOT `.users`), and the raw rows arrive in snake_case from the
  // backend (user_guid, display_name, profile_image_url, …). Mirrors
  // the MiniProjectTeamTab.tsx mapping line 491–504 — keep the two in
  // step. Without this normalization the typeahead returns zero results
  // because L4eUser.userGuid is empty for every row.
  const { securityUsers } = useApiSecurityData();

  /* ── Re-render triggers ── */

  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener("l4e_miniproject_roles_changed", bump);
    return () => window.removeEventListener("l4e_miniproject_roles_changed", bump);
  }, []);

  // DEV-1088 bugfix — listen for SignalR-pushed SecurityUpdated and
  // WorkflowUpdated events from other browsers (the invitee accepting
  // on their machine, another Lead session revoking, etc.) and refetch.
  // Without this the Lead's Settings modal stays on the stale
  // "pending" chip until they close and reopen the modal.
  // The backend respond/invite/revoke flows all emit these via KpiHub.
  useEffect(() => {
    const refetch = () => {
      void refetchMiniProjectRoles(miniProjectGuid);
    };
    window.addEventListener("l4e_security_updated", refetch);
    window.addEventListener("l4e_workflows_updated", refetch);
    return () => {
      window.removeEventListener("l4e_security_updated", refetch);
      window.removeEventListener("l4e_workflows_updated", refetch);
    };
  }, [miniProjectGuid]);

  useEffect(() => {
    void hydrateMiniProjectRolesFromApi(miniProjectGuid);
  }, [miniProjectGuid]);

  const roles = useMemo(
    () => getRolesForMiniProject(miniProjectGuid),
    // version is read so the memo re-evaluates on the change event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [miniProjectGuid, version],
  );

  /* ── User lookup ── */

  const allUsers = useMemo<L4eUser[]>(() => {
    const rows = (securityUsers || []) as unknown as Array<Record<string, unknown>>;
    return rows
      .map((u) => ({
        userGuid: String(u.user_guid || u.userGuid || ""),
        displayName: (u.display_name ?? u.displayName ?? null) as string | null,
        name: (u.name ?? null) as string | null,
        email: (u.email ?? null) as string | null,
        screenName: (u.screen_name ?? u.screenName ?? null) as string | null,
        profileImageUrl: (u.profile_image_url ?? u.profileImageUrl ?? null) as string | null,
      }))
      .filter((u) => !!u.userGuid);
  }, [securityUsers]);

  const userByGuid = useMemo(() => {
    const m = new Map<string, L4eUser>();
    for (const u of allUsers) m.set(u.userGuid.toUpperCase(), u);
    return m;
  }, [allUsers]);

  /* ── Tray state ── */

  const [trayMode, setTrayMode] = useState<TrayMode>(null);
  const trayOpen = trayMode !== null;
  const closeTray = useCallback(() => setTrayMode(null), []);

  const editingRole = useMemo(() => {
    if (trayMode && typeof trayMode === "object" && "editRoleGuid" in trayMode) {
      return roles.find((r) => r.roleGuid === trayMode.editRoleGuid) || null;
    }
    return null;
  }, [trayMode, roles]);

  const invitingRole = useMemo(() => {
    if (trayMode && typeof trayMode === "object" && "inviteToRoleGuid" in trayMode) {
      return roles.find((r) => r.roleGuid === trayMode.inviteToRoleGuid) || null;
    }
    return null;
  }, [trayMode, roles]);

  /* ── Assignee-detail tray context ──
   *  Derives the role + assignee + pending flag + team-row for the
   *  selected assignee chip. Recomputed whenever the role list or team
   *  store refreshes, so a SignalR push (role removed / team hidden
   *  toggled elsewhere) is reflected without closing the tray.            */
  const [teamStoreVersion, setTeamStoreVersion] = useState(0);
  useEffect(() => {
    const bump = () => setTeamStoreVersion((v) => v + 1);
    window.addEventListener("l4e_miniproject_team_changed", bump);
    window.addEventListener("l4e_miniproject_team_persisted", bump);
    return () => {
      window.removeEventListener("l4e_miniproject_team_changed", bump);
      window.removeEventListener("l4e_miniproject_team_persisted", bump);
    };
  }, []);

  const assigneeTrayContext = useMemo(() => {
    if (
      !trayMode
      || typeof trayMode !== "object"
      || !("assigneeAssignmentGuid" in trayMode)
    ) {
      return null;
    }
    const role = roles.find((r) => r.roleGuid === trayMode.assigneeRoleGuid);
    if (!role) return null;
    const active = role.activeAssignees.find(
      (a) => a.assignmentGuid === trayMode.assigneeAssignmentGuid,
    );
    const pending = role.pendingInvitations.find(
      (a) => a.assignmentGuid === trayMode.assigneeAssignmentGuid,
    );
    const assignee = active ?? pending ?? null;
    if (!assignee) return null;
    // Look up the team row for this user. May be null if the user is on
    // a pending invitation (hasn't accepted, so not yet auto-added to
    // the team). The hide checkbox is disabled in that case because
    // there is no team row to flip.
    const team = getTeamForMiniProject(miniProjectGuid);
    const teamRow = team.find(
      (m) => m.userGuid.toUpperCase() === assignee.userGuid.toUpperCase(),
    ) ?? null;
    return { role, assignee, isPending: active == null, teamRow };
    // teamStoreVersion is a deliberate dep so a team-store change refreshes
    // the cached teamRow without forcing the tray to close/reopen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trayMode, roles, miniProjectGuid, teamStoreVersion]);

  /* ── Mutation handlers ── */

  const handleSaveNewRole = useCallback(
    async (draft: RoleDraft) => {
      const headcount = draft.headcountNeeded.trim();
      const headcountInt = headcount ? parseInt(headcount, 10) : null;
      if (headcountInt !== null && (!Number.isFinite(headcountInt) || headcountInt <= 0)) {
        // Surface inline validation in the form rather than 500-ing the
        // server. The server also rejects but this catches client typos.
        return;
      }
      try {
        const saved = await createMiniProjectRole(miniProjectGuid, {
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          responsibilities: draft.responsibilities.trim() || null,
          skillsDesired: draft.skillsDesired.trim() || null,
          headcountNeeded: headcountInt,
        });
        upsertRole(miniProjectGuid, saved);
        closeTray();
      } catch (err) {
        console.error("[MiniProjectRolesSection] createRole failed", err);
      }
    },
    [miniProjectGuid, closeTray],
  );

  const handleSaveExistingRole = useCallback(
    async (roleGuid: string, draft: RoleDraft) => {
      const headcount = draft.headcountNeeded.trim();
      const headcountInt = headcount ? parseInt(headcount, 10) : null;
      try {
        const saved = await updateMiniProjectRole(miniProjectGuid, roleGuid, {
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          responsibilities: draft.responsibilities.trim() || null,
          skillsDesired: draft.skillsDesired.trim() || null,
          headcountNeeded: headcountInt,
        });
        upsertRole(miniProjectGuid, saved);
        closeTray();
      } catch (err) {
        console.error("[MiniProjectRolesSection] updateRole failed", err);
      }
    },
    [miniProjectGuid, closeTray],
  );

  const handleDeleteRole = useCallback(
    async (roleGuid: string) => {
      if (!window.confirm(t("miniProjectRoles.confirmDeleteRole"))) return;
      try {
        await deleteMiniProjectRole(miniProjectGuid, roleGuid);
        removeRole(miniProjectGuid, roleGuid);
        closeTray();
      } catch (err) {
        console.error("[MiniProjectRolesSection] deleteRole failed", err);
      }
    },
    [miniProjectGuid, closeTray, t],
  );

  const handleInvite = useCallback(
    async (roleGuid: string, userGuid: string, message: string) => {
      try {
        await inviteToMiniProjectRole(miniProjectGuid, roleGuid, {
          userGuid,
          message: message.trim() || null,
        });
        // Refetch instead of optimistic update — the server response is
        // a single MiniProjectRoleAssignee, not the role envelope, so a
        // refetch is cheaper than re-assembling the role row locally.
        await refetchMiniProjectRoles(miniProjectGuid);
        closeTray();
      } catch (err) {
        console.error("[MiniProjectRolesSection] invite failed", err);
      }
    },
    [miniProjectGuid, closeTray],
  );

  const handleEndAssignment = useCallback(
    async (role: MiniProjectRole, assignee: MiniProjectRoleAssignee) => {
      const isPending = assignee.status === "pending_invitation";
      const confirmKey = isPending
        ? "miniProjectRoles.confirmRevokeInvite"
        : "miniProjectRoles.confirmRemoveAssignee";
      if (!window.confirm(t(confirmKey, { name: assignee.displayName }))) return;
      try {
        await endMiniProjectRoleAssignment(
          miniProjectGuid,
          role.roleGuid,
          assignee.assignmentGuid,
        );
        await refetchMiniProjectRoles(miniProjectGuid);
      } catch (err) {
        console.error("[MiniProjectRolesSection] endAssignment failed", err);
      }
    },
    [miniProjectGuid, t],
  );

  /* ── Assignee-detail tray: persist hide-from-public toggle ──
   *  Skip's UX answer: the checkbox flips the underlying team-row
   *  is_hidden (matches the team-member tray's hide checkbox exactly).
   *  Per-mutation persist via updateMiniProjectTeamMember — distinct
   *  from the Members section's bulk-save model. We mirror the API
   *  result back into the local team store so the Team list re-renders
   *  with the new is_hidden value without waiting for SignalR.            */
  const handleSaveAssigneeHidden = useCallback(
    async (teamRow: LocalMiniProjectTeamMember, nextIsHidden: boolean) => {
      if (teamRow.isHidden === nextIsHidden) return;
      try {
        const updated = await updateMiniProjectTeamMember(
          miniProjectGuid,
          teamRow.teamMemberGuid,
          { isHidden: nextIsHidden },
        );
        upsertTeamMember(miniProjectGuid, {
          ...teamRow,
          ...updated,
        });
        // Surface the change to the Members section above us. Same event
        // the store fires on bulk save; consumers don't need to know
        // which path the mutation took.
        window.dispatchEvent(new Event("l4e_miniproject_team_changed"));
      } catch (err) {
        console.error("[MiniProjectRolesSection] updateTeamMember hidden failed", err);
      }
    },
    [miniProjectGuid],
  );

  /* ── Render ── */

  return (
    <div style={styles.section}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>
            {t("miniProjectRoles.title")}
            <span style={styles.counter}>
              {t("miniProjectRoles.counter", { count: roles.length })}
            </span>
          </div>
          <div style={styles.subtitle}>{t("miniProjectRoles.subtitle")}</div>
        </div>
        <button
          type="button"
          style={styles.addButton}
          onClick={() => setTrayMode("add-role")}
        >
          {t("miniProjectRoles.addRole")}
        </button>
      </div>

      {roles.length === 0 ? (
        <div style={styles.emptyState}>{t("miniProjectRoles.emptyState")}</div>
      ) : (
        <div style={styles.list}>
          {roles.map((role) => (
            <RoleCard
              key={role.roleGuid}
              role={role}
              userByGuid={userByGuid}
              currentUserGuid={currentUserGuid}
              onEdit={() => setTrayMode({ editRoleGuid: role.roleGuid })}
              onInvite={() => setTrayMode({ inviteToRoleGuid: role.roleGuid })}
              onOpenAssignee={(assignee) =>
                setTrayMode({
                  assigneeRoleGuid: role.roleGuid,
                  assigneeAssignmentGuid: assignee.assignmentGuid,
                })
              }
              t={t}
            />
          ))}
        </div>
      )}

      <Tray
        open={trayOpen}
        onClose={closeTray}
        title={
          trayMode === "add-role"
            ? t("miniProjectRoles.tray.addTitle")
            : editingRole
              ? t("miniProjectRoles.tray.editTitle", { name: editingRole.name })
              : invitingRole
                ? t("miniProjectRoles.tray.inviteTitle", { name: invitingRole.name })
                : assigneeTrayContext
                  ? t("miniProjectRoles.tray.assigneeTitle", {
                      name: assigneeTrayContext.assignee.displayName,
                    })
                  : ""
        }
      >
        {trayMode === "add-role" && (
          <RoleFormTrayBody
            mode="add"
            onSave={handleSaveNewRole}
            onCancel={closeTray}
            t={t}
          />
        )}
        {editingRole && (
          <RoleFormTrayBody
            mode="edit"
            role={editingRole}
            onSave={(draft) => handleSaveExistingRole(editingRole.roleGuid, draft)}
            onDelete={() => handleDeleteRole(editingRole.roleGuid)}
            onCancel={closeTray}
            t={t}
          />
        )}
        {invitingRole && (
          <InviteToRoleTrayBody
            role={invitingRole}
            allUsers={allUsers}
            currentUserGuid={currentUserGuid}
            onInvite={(userGuid, message) =>
              handleInvite(invitingRole.roleGuid, userGuid, message)
            }
            onCancel={closeTray}
            t={t}
          />
        )}
        {assigneeTrayContext && (
          <AssigneeDetailTrayBody
            role={assigneeTrayContext.role}
            assignee={assigneeTrayContext.assignee}
            isPending={assigneeTrayContext.isPending}
            user={userByGuid.get(
              assigneeTrayContext.assignee.userGuid.toUpperCase(),
            )}
            teamRow={assigneeTrayContext.teamRow}
            onSaveHidden={(next) => {
              if (assigneeTrayContext.teamRow) {
                return handleSaveAssigneeHidden(assigneeTrayContext.teamRow, next);
              }
              return Promise.resolve();
            }}
            onRemove={async () => {
              await handleEndAssignment(
                assigneeTrayContext.role,
                assigneeTrayContext.assignee,
              );
              closeTray();
            }}
            onCancel={closeTray}
            t={t}
          />
        )}
      </Tray>
    </div>
  );
};

export default MiniProjectRolesSection;

/* ──────────────────────────────────────────────────────────────────
 *  Sub-components
 * ────────────────────────────────────────────────────────────────── */

type TFn = (key: string, opts?: Record<string, unknown>) => string;

interface RoleCardProps {
  role: MiniProjectRole;
  userByGuid: Map<string, L4eUser>;
  currentUserGuid: string;
  onEdit: () => void;
  onInvite: () => void;
  /** Skip's UX consistency request — chip click opens the assignee-detail
   *  tray (where Remove now lives), mirroring how team-member cards open
   *  their own edit tray. The legacy inline × button was retired. */
  onOpenAssignee: (assignee: MiniProjectRoleAssignee) => void;
  t: TFn;
}

function initialsFor(displayName: string): string {
  return (displayName.trim()[0] || "?").toUpperCase();
}

function RoleCard({ role, userByGuid, onEdit, onInvite, onOpenAssignee, t }: RoleCardProps) {
  const activeCount = role.activeAssignees.length;
  const pendingCount = role.pendingInvitations.length;
  const showEmptyChips = activeCount === 0 && pendingCount === 0;
  const countLabel =
    role.headcountNeeded != null
      ? t("miniProjectRoles.headcountFilled", {
          needed: role.headcountNeeded,
          filled: activeCount,
        })
      : t("miniProjectRoles.headcountUnspec", { filled: activeCount });

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.cardTitleRow}>
            <span style={styles.cardName}>{role.name}</span>
            {role.playbookRoleKey && (
              <span style={styles.templateChip}>
                {t("miniProjectRoles.fromTemplate")}
              </span>
            )}
            <span style={styles.countChip}>{countLabel}</span>
          </div>
          {role.description && (
            <div style={styles.description}>{role.description}</div>
          )}
        </div>
        <div style={styles.cardActions}>
          <button
            type="button"
            style={styles.iconButton}
            onClick={onEdit}
            aria-label={t("miniProjectRoles.editRoleAria", { name: role.name })}
            title={t("miniProjectRoles.editRole")}
          >
            ✎
          </button>
          <button
            type="button"
            style={styles.inviteButton}
            onClick={onInvite}
          >
            {t("miniProjectRoles.inviteButton")}
          </button>
        </div>
      </div>

      {showEmptyChips ? (
        <div style={styles.chipsEmpty}>{t("miniProjectRoles.noAssignees")}</div>
      ) : (
        <div style={styles.chipsRow}>
          {role.activeAssignees.map((a) => (
            <AssigneeChip
              key={a.assignmentGuid}
              assignee={a}
              user={userByGuid.get(a.userGuid.toUpperCase())}
              isPending={false}
              onOpen={() => onOpenAssignee(a)}
              t={t}
            />
          ))}
          {role.pendingInvitations.map((a) => (
            <AssigneeChip
              key={a.assignmentGuid}
              assignee={a}
              user={userByGuid.get(a.userGuid.toUpperCase())}
              isPending
              onOpen={() => onOpenAssignee(a)}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface AssigneeChipProps {
  assignee: MiniProjectRoleAssignee;
  user: L4eUser | undefined;
  isPending: boolean;
  /** Clicking the chip opens the assignee-detail tray. Remove was moved
   *  into the tray's footer for UX consistency with the team-member tray. */
  onOpen: () => void;
  t: TFn;
}

function AssigneeChip({ assignee, user, isPending, onOpen, t }: AssigneeChipProps) {
  const displayName =
    assignee.displayName ||
    user?.displayName ||
    user?.name ||
    `${assignee.userGuid.slice(0, 8)}…`;
  // chip is the visual base. chipPending overrides only border styles, so
  // spreading both retains the base background. The chip is now a <button>
  // so we also normalize font + text-align to match span behavior.
  const chipStyle: React.CSSProperties = {
    ...(isPending ? { ...styles.chip, ...styles.chipPending } : styles.chip),
    cursor: "pointer",
    font: "inherit",
    textAlign: "start",
  };
  const statusStyle = isPending
    ? { ...styles.chipStatus, ...styles.chipStatusPending }
    : { ...styles.chipStatus, ...styles.chipStatusActive };
  return (
    <button
      type="button"
      style={chipStyle}
      onClick={onOpen}
      aria-label={t("miniProjectRoles.openAssigneeAria", { name: displayName })}
      title={t("miniProjectRoles.openAssignee")}
    >
      <span style={styles.chipAvatar}>
        {user?.profileImageUrl ? (
          <img
            src={mediaSrc(user.profileImageUrl) ?? undefined}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
          />
        ) : (
          initialsFor(displayName)
        )}
      </span>
      <span style={styles.chipName}>{displayName}</span>
      <span style={statusStyle}>
        {isPending
          ? t("miniProjectRoles.statusPending")
          : t("miniProjectRoles.statusActive")}
      </span>
    </button>
  );
}

/* ── Tray body: Add/Edit role form ───────────────────────────── */

interface RoleFormTrayBodyProps {
  mode: "add" | "edit";
  role?: MiniProjectRole;
  onSave: (draft: RoleDraft) => void;
  onDelete?: () => void;
  onCancel: () => void;
  t: TFn;
}

function RoleFormTrayBody({ mode, role, onSave, onDelete, onCancel, t }: RoleFormTrayBodyProps) {
  const [draft, setDraft] = useState<RoleDraft>(() => ({
    name: role?.name ?? "",
    description: role?.description ?? "",
    responsibilities: role?.responsibilities ?? "",
    skillsDesired: role?.skillsDesired ?? "",
    headcountNeeded: role?.headcountNeeded != null ? String(role.headcountNeeded) : "",
  }));

  const nameTrimmed = draft.name.trim();
  const descLen = draft.description.length;
  const headcountValue = draft.headcountNeeded.trim();
  const headcountValid =
    headcountValue === "" ||
    (/^[0-9]+$/.test(headcountValue) && parseInt(headcountValue, 10) > 0);
  const canSave = nameTrimmed.length > 0 && descLen <= DESC_MAX && headcountValid;

  return (
    <>
      <Tray.Body>
        <div style={styles.trayBody}>
          <div>
            <div style={styles.fieldLabel}>{t("miniProjectRoles.form.name")}</div>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              style={styles.inlineInput}
              maxLength={200}
              autoFocus
            />
          </div>

          <div>
            <div style={styles.fieldLabel}>
              {t("miniProjectRoles.form.description")}
              <span style={styles.fieldHelp}>
                {t("miniProjectRoles.form.descriptionLimit", { count: DESC_MAX })}
              </span>
            </div>
            <textarea
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              style={styles.textarea}
              maxLength={DESC_MAX}
            />
          </div>

          <div>
            <div style={styles.fieldLabel}>
              {t("miniProjectRoles.form.responsibilities")}
              <span style={styles.fieldHelp}>
                {t("miniProjectRoles.form.onePerLine")}
              </span>
            </div>
            <textarea
              value={draft.responsibilities}
              onChange={(e) =>
                setDraft((d) => ({ ...d, responsibilities: e.target.value }))
              }
              style={styles.textarea}
            />
          </div>

          <div>
            <div style={styles.fieldLabel}>
              {t("miniProjectRoles.form.skillsDesired")}
              <span style={styles.fieldHelp}>
                {t("miniProjectRoles.form.onePerLine")}
              </span>
            </div>
            <textarea
              value={draft.skillsDesired}
              onChange={(e) => setDraft((d) => ({ ...d, skillsDesired: e.target.value }))}
              style={styles.textarea}
            />
          </div>

          <div>
            <div style={styles.fieldLabel}>{t("miniProjectRoles.form.headcount")}</div>
            <input
              type="number"
              min={1}
              value={draft.headcountNeeded}
              onChange={(e) => setDraft((d) => ({ ...d, headcountNeeded: e.target.value }))}
              style={{ ...styles.inlineInput, width: 100 }}
            />
            <div style={styles.helpText}>{t("miniProjectRoles.form.headcountHelp")}</div>
          </div>
        </div>
      </Tray.Body>
      <Tray.Footer>
        <div style={styles.trayFooter}>
          <div>
            {mode === "edit" && onDelete && (
              <button
                type="button"
                style={{ ...styles.smallButton, ...styles.dangerButton }}
                onClick={onDelete}
              >
                {t("miniProjectRoles.form.deleteRole")}
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={styles.smallButton} onClick={onCancel}>
              {t("miniProjectRoles.cancel")}
            </button>
            <button
              type="button"
              style={{
                ...styles.primaryButton,
                ...(canSave ? {} : { opacity: 0.5, cursor: "not-allowed" }),
              }}
              onClick={() => canSave && onSave(draft)}
              disabled={!canSave}
            >
              {mode === "add"
                ? t("miniProjectRoles.form.saveRole")
                : t("miniProjectRoles.form.saveChanges")}
            </button>
          </div>
        </div>
      </Tray.Footer>
    </>
  );
}

/* ── Tray body: Invite to role ─────────────────────────────── */

interface InviteToRoleTrayBodyProps {
  role: MiniProjectRole;
  allUsers: L4eUser[];
  currentUserGuid: string;
  onInvite: (userGuid: string, message: string) => void;
  onCancel: () => void;
  t: TFn;
}

function InviteToRoleTrayBody({
  role,
  allUsers,
  currentUserGuid,
  onInvite,
  onCancel,
  t,
}: InviteToRoleTrayBodyProps) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<L4eUser | null>(null);
  const [message, setMessage] = useState("");

  // Exclude users who already have an open assignment (pending or
  // active) for THIS role, plus the current user (inviting yourself
  // is fine in principle but produces an odd UX — the Lead becomes the
  // role holder via a self-invitation roundtrip rather than directly
  // adding themselves). Easy to relax later.
  const excludeSet = useMemo(() => {
    const s = new Set<string>();
    for (const a of role.activeAssignees) s.add(a.userGuid.toUpperCase());
    for (const a of role.pendingInvitations) s.add(a.userGuid.toUpperCase());
    s.add(currentUserGuid.toUpperCase());
    return s;
  }, [role.activeAssignees, role.pendingInvitations, currentUserGuid]);

  const userOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const pool = allUsers.filter((u) => !excludeSet.has(u.userGuid.toUpperCase()));
    const filtered = pool.filter(
      (u) =>
        (u.displayName ?? "").toLowerCase().includes(q) ||
        (u.name ?? "").toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.screenName ?? "").toLowerCase().includes(q),
    );
    return filtered
      .slice()
      .sort((a, b) =>
        (a.displayName || a.name || a.email || "").localeCompare(
          b.displayName || b.name || b.email || "",
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
      )
      .slice(0, 50);
  }, [allUsers, excludeSet, search]);

  const canSend = selected !== null;

  return (
    <>
      <Tray.Body>
        <div style={styles.trayBody}>
          <div style={styles.helpText}>{t("miniProjectRoles.invite.help")}</div>

          <div>
            <div style={styles.fieldLabel}>{t("miniProjectRoles.invite.searchLabel")}</div>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelected(null);
              }}
              placeholder={t("miniProjectRoles.invite.searchPlaceholder")}
              style={styles.inlineInput}
              autoFocus
            />
          </div>

          {selected ? (
            <div
              style={{
                ...styles.userResultItem,
                background: "var(--bg-elevated)",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <div style={styles.userResultAvatar}>
                {selected.profileImageUrl ? (
                  <img
                    src={mediaSrc(selected.profileImageUrl) ?? undefined}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  (selected.displayName || selected.name || selected.email || "?")
                    .trim()
                    .charAt(0)
                    .toUpperCase()
                )}
              </div>
              <div style={styles.userResultText}>
                <div style={styles.userResultName}>
                  {selected.displayName ||
                    selected.name ||
                    selected.screenName ||
                    `${selected.userGuid.slice(0, 8)}…`}
                </div>
                {selected.email && (
                  <div style={styles.userResultEmail}>{selected.email}</div>
                )}
              </div>
              <button
                type="button"
                style={{ ...styles.smallButton, padding: "4px 8px" }}
                onClick={() => {
                  setSelected(null);
                  setSearch("");
                }}
              >
                {t("miniProjectRoles.invite.change")}
              </button>
            </div>
          ) : (
            <div style={styles.userResults}>
              {userOptions.length === 0 ? (
                <div style={styles.userResultEmpty}>
                  {search.trim()
                    ? t("miniProjectRoles.invite.noResults")
                    : t("miniProjectRoles.invite.startTyping")}
                </div>
              ) : (
                userOptions.map((u) => (
                  <button
                    type="button"
                    key={u.userGuid}
                    style={styles.userResultItem}
                    onClick={() => setSelected(u)}
                  >
                    <div style={styles.userResultAvatar}>
                      {u.profileImageUrl ? (
                        <img
                          src={mediaSrc(u.profileImageUrl) ?? undefined}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        (u.displayName || u.name || u.email || "?")
                          .trim()
                          .charAt(0)
                          .toUpperCase()
                      )}
                    </div>
                    <div style={styles.userResultText}>
                      <div style={styles.userResultName}>
                        {u.displayName ||
                          u.name ||
                          u.screenName ||
                          `${u.userGuid.slice(0, 8)}…`}
                      </div>
                      {u.email && <div style={styles.userResultEmail}>{u.email}</div>}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          <div>
            <div style={styles.fieldLabel}>
              {t("miniProjectRoles.invite.messageLabel")}
              <span style={styles.fieldHelp}>{t("miniProjectRoles.invite.messageHint")}</span>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("miniProjectRoles.invite.messagePlaceholder")}
              style={styles.textarea}
              maxLength={2000}
            />
          </div>
        </div>
      </Tray.Body>
      <Tray.Footer>
        <div style={{ ...styles.trayFooter, justifyContent: "flex-end" }}>
          <button type="button" style={styles.smallButton} onClick={onCancel}>
            {t("miniProjectRoles.cancel")}
          </button>
          <button
            type="button"
            style={{
              ...styles.primaryButton,
              ...(canSend ? {} : { opacity: 0.5, cursor: "not-allowed" }),
            }}
            onClick={() => canSend && selected && onInvite(selected.userGuid, message)}
            disabled={!canSend}
          >
            {t("miniProjectRoles.invite.send")}
          </button>
        </div>
      </Tray.Footer>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 *  Tray body: Assignee detail
 *
 *  Skip's UX consistency request — opening an assignee chip should
 *  feel like opening a team-member card: FlipTeamMemberCard preview
 *  (with the impact signature on the back face), the role name shown
 *  as a read-only label, a hide-from-public checkbox that flips the
 *  underlying mini_project_team_members.is_hidden, and Remove /
 *  Cancel / Save in the footer.
 *
 *  Persistence is per-mutation (matches the rest of this section's
 *  immediate-commit model) — distinct from the Team tab's bulk-save.
 *  The handler in the parent updates both the API and the in-memory
 *  team store so the Members list above re-renders correctly.
 *
 *  Pending-invitation tray: same layout, hide checkbox disabled (no
 *  team row exists yet to flip), Remove labeled "Revoke invitation."
 * ────────────────────────────────────────────────────────────────── */

interface AssigneeDetailTrayBodyProps {
  role: MiniProjectRole;
  assignee: MiniProjectRoleAssignee;
  isPending: boolean;
  user: L4eUser | undefined;
  teamRow: LocalMiniProjectTeamMember | null;
  onSaveHidden: (nextIsHidden: boolean) => Promise<void> | void;
  onRemove: () => Promise<void> | void;
  onCancel: () => void;
  t: TFn;
}

function AssigneeDetailTrayBody({
  role,
  assignee,
  isPending,
  user,
  teamRow,
  onSaveHidden,
  onRemove,
  onCancel,
  t,
}: AssigneeDetailTrayBodyProps) {
  const initialHidden = teamRow?.isHidden ?? false;
  const [isHidden, setIsHidden] = useState(initialHidden);
  const previewName =
    user?.displayName || user?.name || user?.screenName || assignee.displayName;
  const dirty = isHidden !== initialHidden;
  const canSave = !isPending && teamRow != null && dirty;

  const handleSave = async () => {
    if (!canSave) return;
    await onSaveHidden(isHidden);
    onCancel();
  };

  return (
    <>
      <Tray.Body>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "8px 4px 4px" }}>
          {/* Live preview — same FlipTeamMemberCard the public sees on
              the About-tab roster. Flip reveals the L4E impact signature. */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <FlipTeamMemberCard
              displayName={previewName}
              photoUrl={user?.profileImageUrl || undefined}
              roleTitle={role.name}
              linkedUserGuid={user?.userGuid || assignee.userGuid}
              userGuid={user?.userGuid || assignee.userGuid}
              contactEmail={user?.email || undefined}
              backFaceBadge={
                isPending
                  ? t("miniProjectRoles.statusPending")
                  : t("miniProjectRoles.statusActive")
              }
              avatarVariant="l4e"
              splitName
              size="lg"
            />
          </div>

          {/* Role name — read-only label */}
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--text-secondary)",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {t("miniProjectRoles.assigneeTray.roleLabel")}
            </div>
            <div
              style={{
                fontSize: 14,
                color: "var(--text-primary)",
                padding: "8px 10px",
                background: "var(--bg-tertiary)",
                borderRadius: 6,
                border: "1px solid var(--border-default)",
              }}
            >
              {role.name}
            </div>
          </div>

          {/* Hide checkbox — flips mini_project_team_members.is_hidden.
              Disabled for pending invitations (no team row to flip). */}
          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              opacity: isPending ? 0.55 : 1,
              cursor: isPending ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={isHidden}
              onChange={(e) => setIsHidden(e.target.checked)}
              disabled={isPending}
              style={{ marginBlockStart: 3 }}
            />
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                {t("miniProjectRoles.assigneeTray.hideLabel")}
              </span>
              <span style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.4 }}>
                {isPending
                  ? t("miniProjectRoles.assigneeTray.hidePendingHelp")
                  : t("miniProjectRoles.assigneeTray.hideHelp")}
              </span>
            </span>
          </label>
        </div>
      </Tray.Body>
      <Tray.Footer>
        <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
          <button
            type="button"
            onClick={onRemove}
            style={{
              padding: "6px 12px",
              border: "1px solid var(--color-error)",
              background: "transparent",
              color: "var(--color-error)",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {isPending
              ? t("miniProjectRoles.assigneeTray.revoke")
              : t("miniProjectRoles.assigneeTray.remove")}
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "6px 12px",
              border: "1px solid var(--border-default)",
              background: "transparent",
              color: "var(--text-primary)",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {t("miniProjectRoles.assigneeTray.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            style={{
              padding: "6px 14px",
              border: "none",
              background: canSave ? "var(--accent-default)" : "var(--bg-tertiary)",
              color: canSave ? "#fff" : "var(--text-muted)",
              borderRadius: 6,
              cursor: canSave ? "pointer" : "not-allowed",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {t("miniProjectRoles.assigneeTray.save")}
          </button>
        </div>
      </Tray.Footer>
    </>
  );
}
